import { EventsManager } from '@game-libs/events-manager';

export type TimedEvent = {
  id: string;
  iconId?: string;
  categoryId: string;
  readonly duration: number;
  elapsed: number;
  rate?: number;
  once?: boolean;
  done?: boolean;
  paused?: boolean;
  emitId?: string;
  group?: string;
  pauseGroupOnActivation?: boolean;
  // isHidden: boolean;
  // isMasked: boolean;
  // hideUntil?: number;
  // maskUntil?: number;
  // pauseWhenHidden?: boolean;
};

export type TimedEventsOrchestrator = {
  addEvents: (events: TimedEvent[]) => void;
  removeEvent: (id: string) => void;
  clearEvents: () => void;
  setEventRate: (id: string, rate: number) => void;
  loadEvents: (events: TimedEvent[]) => void;
  pauseGroup: (group: string) => void;
  unpauseGroup: (group: string) => void;
  unpauseAll: () => void;
  get allEvents(): TimedEvent[];
  get unfinishedEvents(): TimedEvent[];
  update: (tickDuration?: number) => void;
  subscribe: (cb: () => void) => () => void;
};

export const TimedEventsOrchestrator = (): TimedEventsOrchestrator => {
  let instance: TimedEventsOrchestrator | null = (window as any).timed_events_orchestrator || null;
  if (instance) {
    return instance;
  }

  const { emit } = EventsManager();

  const listeners: (() => void)[] = [];
  let events = new Set<TimedEvent>();
  const pausedGroups = new Set<string>();
  const eventsToReset = new Set<TimedEvent>();

  const setEventRate = (eventId: string, rate: number): void => {
    events.forEach((event) => {
      if (event.id === eventId) {
        event.rate = rate ?? 1;
      }
    });
  };

  const addEvents = (eventsToAdd: TimedEvent[]): void => {
    eventsToAdd.forEach((event) => {
      event.rate = event.rate ?? 1;
      events.add(event);
    });
  };

  const removeEvent = (eventId: string): void => {
    // Remove the event from the events set if its id matches eventId
    events.forEach((event) => {
      if (event.id === eventId) {
        events.delete(event);
      }
    });
  };

  const pauseGroup = (group: string) => {
    events.forEach((event) => {
      if (event.group === group) {
        event.paused = true;
      }
    });
    pausedGroups.add(group);
  };

  const unpauseGroup = (group: string) => {
    events.forEach((event) => {
      if (event.group === group) {
        event.paused = false;
      }
    });

    pausedGroups.delete(group);
  };

  const unpauseAll = () => {
    events.forEach((event) => {
      event.paused = false;
    });

    pausedGroups.clear();
  };

  const clearEvents = () => {
    events.clear();
    eventsToReset.clear();
  };

  const loadEvents = (events: TimedEvent[]) => {
    clearEvents();
    addEvents(events);
  };

  const update = (tickDuration = 1) => {
    // Reset events that were marked for reset from the previous tick
    eventsToReset.forEach((event) => {
      event.elapsed = -1 * tickDuration * event.rate!;
    });
    eventsToReset.clear();

    let hasUpdates = false;
    const eventsToEmit: string[] = [];

    events.forEach((event) => {
      if (!event.done && (!event.group || !pausedGroups.has(event.group))) {
        event.elapsed += tickDuration * event.rate!;
        hasUpdates = true;

        if (event.elapsed >= event.duration) {
          if (event.once) {
            event.done = true;
          } else {
            // Mark for reset on next tick instead of resetting immediately
            eventsToReset.add(event);
          }

          // Collect events to emit instead of emitting immediately
          // This prevents React concurrent rendering errors when emit handlers trigger state updates
          eventsToEmit.push(event.emitId || event.id);

          if (event.pauseGroupOnActivation && event.group) {
            pauseGroup(event.group);
          }
        }
      }
    });

    // Defer emit calls to avoid triggering state updates during render
    // This prevents React concurrent rendering errors
    if (eventsToEmit.length > 0) {
      queueMicrotask(() => {
        eventsToEmit.forEach((eventId) => {
          emit(eventId);
        });
      });
    }

    // Defer listener calls to avoid triggering state updates during render
    // This prevents React concurrent rendering errors
    if (hasUpdates && listeners.length > 0) {
      queueMicrotask(() => {
        listeners.forEach((cb) => cb());
      });
    }
  };

  // Add listeners to be called when this manager updates
  const subscribe = (cb: () => void) => {
    listeners.push(cb);
    return () => {
      const index = listeners.indexOf(cb);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  };

  instance = {
    addEvents,
    pauseGroup,
    unpauseGroup,
    unpauseAll,
    clearEvents,
    setEventRate,
    loadEvents,
    removeEvent,
    get allEvents() {
      return Array.from(events);
    },
    get unfinishedEvents() {
      return Array.from(events).filter((event) => !event.done);
    },
    update,
    subscribe,
  };

  (window as any).timed_events_orchestrator = instance;

  return instance;
};
